import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Copy, Users, Gift, DollarSign, Star, Shield, Clock, TrendingUp, Wallet, Calendar, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import LightLogo from '@/components/LightLogo';
import LightBackground from '@/components/LightBackground';
import { useAuth } from '@/hooks/useAuth';
import { useClientAccess } from '@/hooks/useClientAccess';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface AffiliateStats {
  monthly_active: number;
  monthly_inactive: number;
  lifetime_referrals: number;
  rewards_paid: number;
  rewards_pending: number;
}

interface ReferredMember {
  id: string;
  name: string;
  plan: string;
  has_access: boolean;
  subscription_end: string;
  created_at: string;
}

const Affiliates = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { loading: clientAccessLoading, clientInfo } = useClientAccess();
  const [stats, setStats] = useState<AffiliateStats>({
    monthly_active: 0,
    monthly_inactive: 0,
    lifetime_referrals: 0,
    rewards_paid: 0,
    rewards_pending: 0,
  });
  const [referredMembers, setReferredMembers] = useState<ReferredMember[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(true);

  useEffect(() => {
    document.body.style.cursor = 'none';
    return () => {
      document.body.style.cursor = 'auto';
    };
  }, []);

  // Require authentication
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [authLoading, user, navigate]);

  // Fetch affiliate stats
  useEffect(() => {
    const fetchStats = async () => {
      if (!user?.id) return;
      
      try {
        const { data, error } = await supabase.rpc('get_affiliate_stats', {
          p_user_id: user.id
        });
        
        if (error) {
          console.error('Error fetching affiliate stats:', error);
        } else if (data) {
          const statsData = data as unknown as AffiliateStats;
          setStats(statsData);
        }
      } catch (err) {
        console.error('Error fetching affiliate stats:', err);
      } finally {
        setStatsLoading(false);
      }
    };
    
    if (user?.id) {
      fetchStats();
    }
  }, [user?.id]);

  // Fetch referred members
  useEffect(() => {
    const fetchReferredMembers = async () => {
      if (!user?.id) return;
      
      try {
        // First get the client ID for this user
        const { data: clientData, error: clientError } = await supabase
          .from('clients')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (clientError || !clientData) {
          console.error('Error fetching client:', clientError);
          setMembersLoading(false);
          return;
        }
        
        // Now fetch referred members
        const { data, error } = await supabase
          .from('clients')
          .select('id, name, plan, has_access, subscription_end, created_at')
          .eq('referred_by', clientData.id)
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error('Error fetching referred members:', error);
        } else if (data) {
          setReferredMembers(data);
        }
      } catch (err) {
        console.error('Error fetching referred members:', err);
      } finally {
        setMembersLoading(false);
      }
    };
    
    if (user?.id) {
      fetchReferredMembers();
    }
  }, [user?.id]);

  // Generate referral code from user ID (last 12 characters)
  const referralCode = useMemo(() => {
    if (!user?.id) return '';
    const cleanId = user.id.replace(/-/g, '');
    return cleanId.slice(-12).toUpperCase();
  }, [user?.id]);

  // Generate referral link
  const referralLink = useMemo(() => {
    if (!referralCode) return '';
    return `https://facelessaura.club/apply?ref=${referralCode}`;
  }, [referralCode]);

  const copyReferralCode = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode);
      toast.success('Referral code copied to clipboard!');
    }
  };

  const copyReferralLink = () => {
    if (referralLink) {
      navigator.clipboard.writeText(referralLink);
      toast.success('Referral link copied to clipboard!');
    }
  };

  // Helper to format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper to get member status
  const getMemberStatus = (member: ReferredMember) => {
    const isExpired = new Date(member.subscription_end) < new Date();
    if (member.plan === 'Lifetime') return { status: 'Lifetime', color: 'text-neon-purple' };
    if (!member.has_access || isExpired) return { status: 'Inactive', color: 'text-destructive' };
    return { status: 'Active', color: 'text-green-400' };
  };

  // Check if user is active
  const isActive = clientInfo.hasAccess || clientInfo.clientType === 'admin';

  if (authLoading || clientAccessLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-neon-purple" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900 relative overflow-hidden">
      <LightBackground variant="subtle" />

      {/* Header */}
      <motion.header
        className="relative z-20 p-6 flex justify-between items-center border-b border-slate-200"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Link to="/" className="flex items-center">
          <LightLogo size="md" />
        </Link>
        <Button
          variant="ghost"
          className="text-muted-foreground hover:text-neon-purple hover:bg-neon-purple/10"
          onClick={() => navigate('/dashboard')}
          data-hover
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>
      </motion.header>

      {/* Main content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        {/* Title */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <h1 className="text-3xl md:text-4xl font-extralight tracking-[0.15em] mb-4">
            <span className="gradient-text-neon">Affiliate Program</span>
          </h1>
          <p className="text-muted-foreground font-serif italic max-w-2xl mx-auto">
            Share the invisible power. Earn rewards for every member you bring to the club.
          </p>
        </motion.div>

        {/* Stats Dashboard */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          {/* Monthly Active */}
          <div className="glass-panel-neon p-4 rounded-lg text-center">
            <div className="flex justify-center mb-2">
              <Users className="w-5 h-5 text-green-400" />
            </div>
            <p className="text-2xl font-light gradient-text-neon">
              {statsLoading ? '...' : stats.monthly_active}
            </p>
            <p className="text-xs text-muted-foreground tracking-wider uppercase mt-1">
              Monthly Active
            </p>
          </div>

          {/* Monthly Inactive */}
          <div className="glass-panel-neon p-4 rounded-lg text-center">
            <div className="flex justify-center mb-2">
              <Users className="w-5 h-5 text-red-400" />
            </div>
            <p className="text-2xl font-light text-foreground/60">
              {statsLoading ? '...' : stats.monthly_inactive}
            </p>
            <p className="text-xs text-muted-foreground tracking-wider uppercase mt-1">
              Monthly Inactive
            </p>
          </div>

          {/* Lifetime Referrals */}
          <div className="glass-panel-neon p-4 rounded-lg text-center">
            <div className="flex justify-center mb-2">
              <Star className="w-5 h-5 text-neon-purple" />
            </div>
            <p className="text-2xl font-light gradient-text-neon">
              {statsLoading ? '...' : stats.lifetime_referrals}
            </p>
            <p className="text-xs text-muted-foreground tracking-wider uppercase mt-1">
              Lifetime Referrals
            </p>
          </div>

          {/* Rewards Paid */}
          <div className="glass-panel-neon p-4 rounded-lg text-center">
            <div className="flex justify-center mb-2">
              <Wallet className="w-5 h-5 text-green-400" />
            </div>
            <p className="text-2xl font-light text-green-400">
              ${statsLoading ? '...' : stats.rewards_paid.toFixed(2)}
            </p>
            <p className="text-xs text-muted-foreground tracking-wider uppercase mt-1">
              Rewards Paid
            </p>
          </div>

          {/* Rewards Pending */}
          <div className="glass-panel-neon p-4 rounded-lg text-center col-span-2 md:col-span-1">
            <div className="flex justify-center mb-2">
              <TrendingUp className="w-5 h-5 text-neon-blue" />
            </div>
            <p className="text-2xl font-light text-neon-blue">
              ${statsLoading ? '...' : stats.rewards_pending.toFixed(2)}
            </p>
            <p className="text-xs text-muted-foreground tracking-wider uppercase mt-1">
              Pending
            </p>
          </div>
        </motion.div>

        {/* Referred Members List */}
        <motion.div
          className="glass-panel-neon p-6 rounded-lg mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.18 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-5 h-5 text-neon-purple" />
            <h2 className="text-lg font-light tracking-[0.1em] uppercase text-neon-purple">Your Referrals</h2>
          </div>
          
          {membersLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-neon-purple" />
            </div>
          ) : referredMembers.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground text-sm">No referrals yet. Share your code to start earning rewards!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neon-purple/10">
                    <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-muted-foreground font-medium">Member</th>
                    <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-muted-foreground font-medium">Plan</th>
                    <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-muted-foreground font-medium">Status</th>
                    <th className="text-left py-3 px-2 text-xs uppercase tracking-wider text-muted-foreground font-medium">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {referredMembers.map((member) => {
                    const { status, color } = getMemberStatus(member);
                    return (
                      <tr key={member.id} className="border-b border-neon-purple/5 hover:bg-neon-purple/5 transition-colors">
                        <td className="py-3 px-2">
                          <span className="text-foreground">{member.name}</span>
                        </td>
                        <td className="py-3 px-2">
                          <span className={member.plan === 'Lifetime' ? 'text-neon-purple' : 'text-foreground/80'}>
                            {member.plan}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          <span className={`flex items-center gap-1.5 ${color}`}>
                            {status === 'Active' || status === 'Lifetime' ? (
                              <CheckCircle className="w-3.5 h-3.5" />
                            ) : (
                              <XCircle className="w-3.5 h-3.5" />
                            )}
                            {status}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          <span className="flex items-center gap-1.5 text-muted-foreground">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(member.created_at)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>

        {/* Referral Code Box */}
        <motion.div
          className="relative p-8 glass-panel-neon rounded-lg mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {/* Corner decorations */}
          <div className="absolute top-0 left-0 w-6 h-6 border-l border-t border-neon-purple/30" />
          <div className="absolute top-0 right-0 w-6 h-6 border-r border-t border-neon-purple/30" />
          <div className="absolute bottom-0 left-0 w-6 h-6 border-l border-b border-neon-purple/30" />
          <div className="absolute bottom-0 right-0 w-6 h-6 border-r border-b border-neon-purple/30" />

          <div className="text-center">
            <p className="text-xs tracking-[0.3em] uppercase text-neon-purple/60 mb-4">
              Your Unique Referral Code
            </p>
            <div className="flex items-center justify-center gap-4 mb-4">
              <code className="text-2xl md:text-3xl font-mono tracking-[0.2em] gradient-text-neon">
                {referralCode || '---'}
              </code>
              <Button
                variant="ghost"
                size="icon"
                className="text-neon-purple/60 hover:text-neon-purple hover:bg-neon-purple/10"
                onClick={copyReferralCode}
                data-hover
                disabled={!referralCode}
              >
                <Copy className="h-5 w-5" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Share this code with new members when they apply
            </p>
            {!isActive && (
              <p className="text-sm text-amber-400 mt-2">
                ⚠️ You must have an active membership to participate in the affiliate program
              </p>
            )}
          </div>
        </motion.div>

        {/* Referral Link Box */}
        <motion.div
          className="relative p-6 glass-panel-neon rounded-lg mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
        >
          <div className="text-center">
            <p className="text-xs tracking-[0.3em] uppercase text-neon-blue/60 mb-3">
              Your Shareable Referral Link
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <div className="w-full sm:flex-1 max-w-md px-4 py-3 bg-charcoal/50 rounded-lg border border-neon-blue/20 overflow-hidden">
                <code className="text-xs sm:text-sm text-neon-blue break-all">
                  {referralLink || '---'}
                </code>
              </div>
              <Button
                variant="ghost"
                className="text-neon-blue/60 hover:text-neon-blue hover:bg-neon-blue/10 gap-2"
                onClick={copyReferralLink}
                data-hover
                disabled={!referralLink}
              >
                <Copy className="h-4 w-4" />
                <span className="hidden sm:inline">Copy Link</span>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Anyone who signs up through this link will be automatically counted as your referral
            </p>
          </div>
        </motion.div>

        {/* Program Overview */}
        <motion.div
          className="space-y-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {/* Overview Section */}
          <section className="glass-panel-neon p-6 rounded-lg">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-5 h-5 text-neon-purple" />
              <h2 className="text-lg font-light tracking-[0.1em] uppercase text-neon-purple">Program Overview</h2>
            </div>
            <div className="space-y-3 text-sm text-foreground/80">
              <p>Our Affiliate Program is an optional reward system for members who introduce new students to the Academy.</p>
              <p>It is designed as a referral incentive, not as a primary source of income.</p>
              <p className="text-muted-foreground italic">Participation is optional and subject to our Affiliate Terms.</p>
            </div>
          </section>

          {/* Eligibility Section */}
          <section className="glass-panel-neon p-6 rounded-lg">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-5 h-5 text-neon-purple" />
              <h2 className="text-lg font-light tracking-[0.1em] uppercase text-neon-purple">Eligibility</h2>
            </div>
            <ul className="space-y-2 text-sm text-foreground/80">
              <li className="flex items-start gap-2">
                <span className="text-neon-purple mt-1">•</span>
                <span>Only active members can participate.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-neon-purple mt-1">•</span>
                <span>Monthly members must have an active subscription.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-neon-purple mt-1">•</span>
                <span>Lifetime members are considered active permanently.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-neon-purple mt-1">•</span>
                <span>Self-referrals and duplicate accounts are not allowed.</span>
              </li>
            </ul>
          </section>

          {/* Monthly Plan Member Section */}
          <section className="glass-panel-neon p-6 rounded-lg">
            <div className="flex items-center gap-3 mb-4">
              <Gift className="w-5 h-5 text-neon-purple" />
              <h2 className="text-lg font-light tracking-[0.1em] uppercase text-neon-purple">If you are a Monthly Plan Member</h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-foreground mb-2">🎓 Free Monthly Membership</h3>
                <ul className="space-y-2 text-sm text-foreground/80 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>If you refer 5 active monthly members, your own monthly fee is waived.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>Your free access remains valid only while all 5 referrals stay active.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>If any referral becomes inactive, your monthly fee resumes automatically.</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-foreground mb-2">💰 Monthly Referral Reward</h3>
                <ul className="space-y-2 text-sm text-foreground/80 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>After your 5th active monthly referral, you may earn a <strong className="text-neon-purple">10% referral reward</strong> on monthly payments made by your directly referred active members.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>Referral rewards are calculated only on successfully paid invoices.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>If you or the referred member becomes inactive, rewards stop automatically.</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-foreground mb-2">🎓 Free Lifetime Membership and Rewards</h3>
                <ul className="space-y-2 text-sm text-foreground/80 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>If you refer 5 lifetime members, you are granted Lifetime access at no cost.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>Lifetime access, once granted, is permanent.</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-foreground mb-2">💰 Lifetime Referral Reward</h3>
                <ul className="space-y-2 text-sm text-foreground/80 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>Refer 3 or more lifetime members may be invited to the Lifetime Affiliate Partner program.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>Affiliate Partners may earn a <strong className="text-neon-purple">15% referral reward</strong> on each direct lifetime referral.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>This reward applies only to direct referrals and does not include multi-level commissions.</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Lifetime Plan Member Section */}
          <section className="glass-panel-neon p-6 rounded-lg">
            <div className="flex items-center gap-3 mb-4">
              <Star className="w-5 h-5 text-neon-purple" />
              <h2 className="text-lg font-light tracking-[0.1em] uppercase text-neon-purple">If You Are a Lifetime Plan Member</h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-foreground mb-2">💼 Affiliate Partner Eligibility</h3>
                <ul className="space-y-2 text-sm text-foreground/80 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>If you refer 3 or more lifetime members, you may be invited to the Lifetime Affiliate Partner program.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>Lifetime Affiliate Partners may earn a <strong className="text-neon-purple">10% referral reward</strong> on each direct lifetime referral.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>This reward applies only to direct referrals and does not include multi-level commissions.</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-foreground mb-2">💰 Monthly Referral Reward</h3>
                <ul className="space-y-2 text-sm text-foreground/80 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>If you refer 5 active monthly members, you may earn a <strong className="text-neon-purple">10% referral reward</strong> on monthly payments made by your directly referred active monthly members.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>Referral rewards are calculated only on successfully paid invoices.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>If a referred member becomes inactive, rewards related to that member stop automatically.</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-foreground mb-2">🔐 Eligibility & Activity Status</h3>
                <ul className="space-y-2 text-sm text-foreground/80 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>Lifetime Plan Members are considered active permanently.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>Referral rewards apply only while referred members remain active.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-neon-purple mt-1">•</span>
                    <span>Self-referrals and duplicate accounts are not allowed.</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Payments & Processing Section */}
          <section className="glass-panel-neon p-6 rounded-lg">
            <div className="flex items-center gap-3 mb-4">
              <DollarSign className="w-5 h-5 text-neon-purple" />
              <h2 className="text-lg font-light tracking-[0.1em] uppercase text-neon-purple">Payments & Processing</h2>
            </div>
            <ul className="space-y-2 text-sm text-foreground/80">
              <li className="flex items-start gap-2">
                <span className="text-neon-purple mt-1">•</span>
                <span>Referral rewards are subject to a <strong>5-week holding period</strong> to account for refunds and chargebacks.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-neon-purple mt-1">•</span>
                <span>Payments are processed via <strong>PayPal</strong>.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-neon-purple mt-1">•</span>
                <span>Referral rewards are not guaranteed and depend on successful payments and continued eligibility.</span>
              </li>
            </ul>
          </section>

          {/* Important Notes Section */}
          <section className="glass-panel-neon p-6 rounded-lg border border-neon-purple/20">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="w-5 h-5 text-neon-purple" />
              <h2 className="text-lg font-light tracking-[0.1em] uppercase text-neon-purple">Important Notes</h2>
            </div>
            <ul className="space-y-2 text-sm text-foreground/80">
              <li className="flex items-start gap-2">
                <span className="text-neon-purple mt-1">•</span>
                <span>Referral rewards are a secondary benefit and not a guaranteed income.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-neon-purple mt-1">•</span>
                <span>This program does not involve multi-level marketing.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-neon-purple mt-1">•</span>
                <span>We reserve the right to modify or terminate the program at any time.</span>
              </li>
            </ul>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default Affiliates;
